import React from 'react';
import { Link } from 'react-router-dom';

const freeFeatures = [
  { text: '1 farm location', included: true },
  { text: '2 ponds or 10 goats', included: true },
  { text: 'All data entry forms', included: true },
  { text: 'Dashboard', included: true },
  { text: '1 user', included: true },
  { text: 'Financial reports', included: false },
  { text: 'Multiple users', included: false },
  { text: 'Payroll & tasks', included: false },
  { text: 'Priority support', included: false },
];

const paidFeatures = [
  { text: 'Unlimited locations', included: true },
  { text: 'Unlimited ponds & goats', included: true },
  { text: 'All data entry forms', included: true },
  { text: 'Dashboard', included: true },
  { text: 'Multiple users & roles', included: true },
  { text: 'Financial reports & analytics', included: true },
  { text: 'Payroll & task management', included: true },
  { text: 'Retail sales tracking', included: true },
  { text: 'Priority WhatsApp support', included: true },
];

const CheckIcon = () => (
  <svg className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const CrossIcon = () => (
  <svg className="w-4 h-4 text-slate-300 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const Pricing: React.FC = () => {
  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">
            Start free. Upgrade when you grow.
          </h2>
          <p className="mt-3 text-slate-600 text-lg">
            No credit card needed. Use FarmLog free forever with basic features.
          </p>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-3 max-w-5xl mx-auto">

          {/* Free tier */}
          <div className="rounded-2xl bg-white p-7 shadow-sm border border-slate-200 flex flex-col">
            <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Free</p>
            <p className="mt-3 text-4xl font-extrabold text-slate-900">
              Rs0<span className="text-lg font-normal text-slate-500">/forever</span>
            </p>
            <p className="mt-2 text-sm text-slate-500">No card needed</p>
            <div className="mt-6 flex-1">
              <ul className="space-y-2.5">
                {freeFeatures.map((item, i) => (
                  <li key={i} className={`flex items-start gap-2 text-sm ${item.included ? 'text-slate-700' : 'text-slate-400'}`}>
                    {item.included ? <CheckIcon /> : <CrossIcon />}
                    {item.text}
                  </li>
                ))}
              </ul>
            </div>
            <Link to="/demo" className="mt-8 block w-full text-center rounded-xl bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-200 transition border border-slate-200">
              See Demo
            </Link>
          </div>

          {/* Monthly — highlighted */}
          <div className="relative rounded-2xl bg-white p-7 shadow-md border-2 border-amber-400 flex flex-col">
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-amber-500 px-4 py-1 text-xs font-bold text-slate-900 uppercase tracking-wider">
              Most popular
            </div>
            <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Pro Monthly</p>
            <p className="mt-3 text-4xl font-extrabold text-slate-900">
              Rs500<span className="text-lg font-normal text-slate-500">/mo</span>
            </p>
            <p className="mt-2 text-sm text-slate-500">Per farm, cancel anytime</p>
            <div className="mt-6 flex-1">
              <ul className="space-y-2.5">
                {paidFeatures.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckIcon />
                    {item.text}
                  </li>
                ))}
              </ul>
            </div>
            <Link to="/demo" className="mt-8 block w-full text-center rounded-xl bg-amber-500 px-4 py-3 text-sm font-bold text-slate-900 hover:bg-amber-400 transition shadow-md shadow-amber-500/20">
              See Demo
            </Link>
          </div>

          {/* Annual */}
          <div className="relative rounded-2xl bg-white p-7 shadow-sm border border-slate-200 flex flex-col">
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-emerald-600 px-4 py-1 text-xs font-bold text-white uppercase tracking-wider">
              Save 16%
            </div>
            <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Pro Annual</p>
            <p className="mt-3 text-4xl font-extrabold text-slate-900">
              Rs5,000<span className="text-lg font-normal text-slate-500">/yr</span>
            </p>
            <p className="mt-2 text-sm text-emerald-600 font-medium">2 months free</p>
            <div className="mt-6 flex-1">
              <ul className="space-y-2.5">
                {paidFeatures.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckIcon />
                    {item.text}
                  </li>
                ))}
              </ul>
            </div>
            <Link to="/demo" className="mt-8 block w-full text-center rounded-xl bg-slate-800 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-700 transition">
              See Demo
            </Link>
          </div>
        </div>

        {/* ROI note */}
        <div className="mt-12 max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 rounded-xl bg-emerald-50 px-5 py-3 border border-emerald-200">
            <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
            </svg>
            <p className="text-sm text-emerald-800">
              Start free, see value, then upgrade. <strong>Rs500/month</strong> = 1 bag of feed.
              Better tracking saves <strong>Rs2,000+/month</strong> on waste alone.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
